import React, { useState } from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FiHelpCircle, FiArrowUp, FiArrowDown } from "react-icons/fi";

const COUNTRIES = [
  { code: "AF", name: "Afghanistan" }, { code: "AL", name: "Albania" }, { code: "DZ", name: "Algeria" },
  { code: "AR", name: "Argentina" }, { code: "AU", name: "Australia" }, { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" }, { code: "BR", name: "Brazil" }, { code: "BG", name: "Bulgaria" },
  { code: "CA", name: "Canada" }, { code: "CL", name: "Chile" }, { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" }, { code: "HR", name: "Croatia" }, { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" }, { code: "DK", name: "Denmark" }, { code: "EG", name: "Egypt" },
  { code: "EE", name: "Estonia" }, { code: "FI", name: "Finland" }, { code: "FR", name: "France" },
  { code: "GE", name: "Georgia" }, { code: "DE", name: "Germany" }, { code: "GR", name: "Greece" },
  { code: "HU", name: "Hungary" }, { code: "IN", name: "India" }, { code: "ID", name: "Indonesia" },
  { code: "IE", name: "Ireland" }, { code: "IL", name: "Israel" }, { code: "IT", name: "Italy" },
  { code: "JP", name: "Japan" }, { code: "LB", name: "Lebanon" }, { code: "LU", name: "Luxembourg" },
  { code: "MX", name: "Mexico" }, { code: "MD", name: "Moldova" }, { code: "MA", name: "Morocco" },
  { code: "NL", name: "Netherlands" }, { code: "NZ", name: "New Zealand" }, { code: "NO", name: "Norway" },
  { code: "PE", name: "Peru" }, { code: "PL", name: "Poland" }, { code: "PT", name: "Portugal" },
  { code: "RO", name: "Romania" }, { code: "RU", name: "Russia" }, { code: "RS", name: "Serbia" },
  { code: "SK", name: "Slovakia" }, { code: "SI", name: "Slovenia" }, { code: "ZA", name: "South Africa" },
  { code: "KR", name: "South Korea" }, { code: "ES", name: "Spain" }, { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" }, { code: "TH", name: "Thailand" }, { code: "TR", name: "Turkey" },
  { code: "UA", name: "Ukraine" }, { code: "GB", name: "United Kingdom" }, { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" },
];

const dk = (variant) => variant === "dark";

function QuestionWrapper({ question, error, variant, children }) {
  const [showHelp, setShowHelp] = useState(false);
  const dark = dk(variant);
  return (
    <FormControl isInvalid={!!error} mb={6}>
      <HStack align="flex-start" spacing={2} mb={2}>
        <FormLabel mb={0} fontWeight="600" fontSize="md" color={dark ? "white" : "gray.800"} flex="1">
          {question.text}
          {question.validation?.required && (
            <Text as="span" color="red.400" ml={1}>*</Text>
          )}
        </FormLabel>
        {question.help && (
          <Tooltip label={question.help} hasArrow placement="top" isOpen={showHelp}>
            <IconButton
              icon={<FiHelpCircle />}
              size="xs"
              variant="ghost"
              color={dark ? "gray.300" : "gray.400"}
              aria-label="Why we ask this"
              onClick={() => setShowHelp(!showHelp)}
              onBlur={() => setShowHelp(false)}
            />
          </Tooltip>
        )}
      </HStack>
      {showHelp && question.help && (
        <FormHelperText mt={-1} mb={2} fontSize="xs" color={dark ? "gray.300" : "gray.500"}>
          {question.help}
        </FormHelperText>
      )}
      {children}
      {error && <FormErrorMessage>{error === "required" ? "This question is required." : error}</FormErrorMessage>}
    </FormControl>
  );
}

export function SingleSelectQuestion({ question, value, onChange, error, variant }) {
  const selected = value?.selected ?? "";
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <RadioGroup value={selected} onChange={(val) => onChange({ selected: val })}>
        <Stack spacing={2} color={dk(variant) ? "gray.200" : undefined}>
          {(question.options || []).map((opt) => (
            <Radio key={opt.code} value={opt.code} colorScheme="gwbi">
              {opt.label}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    </QuestionWrapper>
  );
}

export function MultiSelectQuestion({ question, value, onChange, error, variant }) {
  const selected = value?.selected ?? [];
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <CheckboxGroup value={selected} onChange={(vals) => onChange({ selected: vals })}>
        <Stack spacing={2} color={dk(variant) ? "gray.200" : undefined}>
          {(question.options || []).map((opt) => (
            <Checkbox key={opt.code} value={opt.code} colorScheme="gwbi">
              {opt.label}
            </Checkbox>
          ))}
        </Stack>
      </CheckboxGroup>
    </QuestionWrapper>
  );
}

export function LikertQuestion({ question, value, onChange, error, variant }) {
  const max = question.qtype === "likert_7" ? 7 : 5;
  const score = value?.score ?? null;
  const meta = question.scale_metadata || {};
  const lowLabel = meta.anchor_low || "Strongly disagree";
  const highLabel = meta.anchor_high || "Strongly agree";
  const dark = dk(variant);

  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <HStack justify="space-between" mb={1}>
        <Text fontSize="xs" color={dark ? "gray.300" : "gray.500"}>{lowLabel}</Text>
        <Text fontSize="xs" color={dark ? "gray.300" : "gray.500"}>{highLabel}</Text>
      </HStack>
      <HStack
        spacing={0}
        justify="center"
        role="radiogroup"
        aria-label={question.text}
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <Box
            key={n}
            as="button"
            type="button"
            role="radio"
            aria-checked={score === n}
            tabIndex={0}
            onClick={() => onChange({ score: n })}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" && n < max) onChange({ score: n + 1 });
              if (e.key === "ArrowLeft" && n > 1) onChange({ score: n - 1 });
            }}
            w={`${100 / max}%`}
            maxW="60px"
            py={3}
            mx={1}
            borderRadius="md"
            textAlign="center"
            fontWeight="600"
            fontSize="sm"
            bg={score === n ? "gwbi.500" : dark ? "whiteAlpha.300" : "gray.100"}
            color={score === n ? "white" : dark ? "gray.200" : "gray.600"}
            _hover={{ bg: score === n ? "gwbi.600" : dark ? "whiteAlpha.400" : "gray.200" }}
            _focus={{ boxShadow: "outline" }}
            transition="all 0.15s"
          >
            {n}
          </Box>
        ))}
      </HStack>
    </QuestionWrapper>
  );
}

export function NumericQuestion({ question, value, onChange, error, variant }) {
  const num = value?.number ?? "";
  const v = question.validation || {};
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <NumberInput
        value={num}
        min={v.min}
        max={v.max}
        onChange={(_, val) => onChange({ number: isNaN(val) ? "" : val })}
      >
        <NumberInputField
          placeholder={v.min != null ? `${v.min}–${v.max}` : ""}
          bg={dk(variant) ? "white" : undefined}
          color={dk(variant) ? "gray.800" : undefined}
        />
      </NumberInput>
    </QuestionWrapper>
  );
}

export function NumericBandQuestion({ question, value, onChange, error, variant }) {
  const selected = value?.band ?? "";
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <RadioGroup value={selected} onChange={(val) => onChange({ band: val })}>
        <Stack spacing={2} color={dk(variant) ? "gray.200" : undefined}>
          {(question.options || []).map((opt) => (
            <Radio key={opt.code} value={opt.code} colorScheme="gwbi">
              {opt.label}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    </QuestionWrapper>
  );
}

export function RankQuestion({ question, value, onChange, error, variant }) {
  const ranking = value?.ranking ?? (question.options || []).map((o) => o.code);
  const labelMap = {};
  for (const o of question.options || []) labelMap[o.code] = o.label;
  const dark = dk(variant);

  const move = (idx, dir) => {
    const next = [...ranking];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange({ ranking: next });
  };

  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <VStack spacing={1} align="stretch">
        {ranking.map((code, idx) => (
          <HStack
            key={code}
            bg={dark ? "whiteAlpha.200" : "gray.50"}
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor={dark ? "whiteAlpha.300" : "gray.200"}
          >
            <Text fontSize="sm" fontWeight="600" color={dark ? "gray.300" : "gray.500"} w="20px">
              {idx + 1}.
            </Text>
            <Text fontSize="sm" flex="1" color={dark ? "gray.100" : undefined}>{labelMap[code] || code}</Text>
            <IconButton
              icon={<FiArrowUp />}
              size="xs"
              variant="ghost"
              color={dark ? "gray.300" : undefined}
              isDisabled={idx === 0}
              onClick={() => move(idx, -1)}
              aria-label={`Move ${labelMap[code]} up`}
            />
            <IconButton
              icon={<FiArrowDown />}
              size="xs"
              variant="ghost"
              color={dark ? "gray.300" : undefined}
              isDisabled={idx === ranking.length - 1}
              onClick={() => move(idx, 1)}
              aria-label={`Move ${labelMap[code]} down`}
            />
          </HStack>
        ))}
      </VStack>
    </QuestionWrapper>
  );
}

export function TextShortQuestion({ question, value, onChange, error, variant }) {
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <Input
        value={value?.text ?? ""}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder={question.help || ""}
        maxLength={question.validation?.max || 500}
        bg={dk(variant) ? "white" : undefined}
        color={dk(variant) ? "gray.800" : undefined}
      />
    </QuestionWrapper>
  );
}

export function TextLongQuestion({ question, value, onChange, error, variant }) {
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <Textarea
        value={value?.text ?? ""}
        onChange={(e) => onChange({ text: e.target.value })}
        placeholder={question.help || ""}
        rows={4}
        maxLength={question.validation?.max || 5000}
        bg={dk(variant) ? "white" : undefined}
        color={dk(variant) ? "gray.800" : undefined}
      />
    </QuestionWrapper>
  );
}

export function BooleanQuestion({ question, value, onChange, error, variant }) {
  const val = value?.value;
  const dark = dk(variant);
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <HStack spacing={4}>
        {[true, false].map((b) => (
          <Box
            key={String(b)}
            as="button"
            type="button"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            fontSize="sm"
            bg={val === b ? "gwbi.500" : dark ? "whiteAlpha.300" : "gray.100"}
            color={val === b ? "white" : dark ? "gray.200" : "gray.600"}
            _hover={{ bg: val === b ? "gwbi.600" : dark ? "whiteAlpha.400" : "gray.200" }}
            onClick={() => onChange({ value: b })}
          >
            {b ? "Yes" : "No"}
          </Box>
        ))}
      </HStack>
    </QuestionWrapper>
  );
}

export function CountryQuestion({ question, value, onChange, error, variant }) {
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <Select
        placeholder="Select country..."
        value={value?.selected ?? ""}
        onChange={(e) => onChange({ selected: e.target.value })}
        bg={dk(variant) ? "white" : undefined}
        color={dk(variant) ? "gray.800" : undefined}
      >
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{c.name}</option>
        ))}
      </Select>
    </QuestionWrapper>
  );
}

export function ConsentToggleQuestion({ question, value, onChange, error, variant }) {
  const granted = value?.granted ?? false;
  const dark = dk(variant);
  return (
    <QuestionWrapper question={question} error={error} variant={variant}>
      <HStack spacing={3}>
        <Switch
          isChecked={granted}
          onChange={(e) => onChange({ granted: e.target.checked })}
          colorScheme="gwbi"
          size="lg"
        />
        <Text fontSize="sm" color={granted ? (dark ? "gwbi.300" : "gwbi.700") : (dark ? "gray.300" : "gray.500")}>
          {granted ? "Consent granted" : "Consent not granted"}
        </Text>
      </HStack>
    </QuestionWrapper>
  );
}

const RENDERER_MAP = {
  single_select: SingleSelectQuestion,
  multi_select: MultiSelectQuestion,
  likert_5: LikertQuestion,
  likert_7: LikertQuestion,
  numeric: NumericQuestion,
  numeric_band: NumericBandQuestion,
  rank: RankQuestion,
  text_short: TextShortQuestion,
  text_long: TextLongQuestion,
  boolean: BooleanQuestion,
  country: CountryQuestion,
  consent_toggle: ConsentToggleQuestion,
};

export function QuestionRenderer({ question, value, onChange, error, variant }) {
  const Component = RENDERER_MAP[question.qtype];
  if (!Component) return <Text color="red.400">Unknown question type: {question.qtype}</Text>;
  return <Component question={question} value={value} onChange={onChange} error={error} variant={variant} />;
}
